'use client'

/**
 * LeagueTable — the generic sortable league table for /data pages
 * (FRIDGE-450: "every table sorts", "every entity name is a link").
 *
 * The last four dead tables (dimensions, space-efficiency, the two
 * wine-beverage tables) were hand-rolled server <table> markup: rows already
 * linked their entities but nothing sorted. SortableTable's column defs carry
 * functions, which cannot cross the server→client boundary — so pages pass
 * PLAIN data (column descriptors + cell values + an optional href per row)
 * and this wrapper builds the functions. Same boundary pattern as
 * series-tables and preview-table.
 *
 * Cells are pre-formatted strings paired with a raw sort value, so the page
 * keeps using its own formatters (formatIn, formatSpec, …) server-side and
 * the client ships zero formatting logic.
 */

import Link from 'next/link'
import { SortableTable } from './sortable-table'

export interface LeagueCell {
  /** Pre-formatted display string (the page's own formatter output). */
  text: string
  /** Raw value for sorting; falls back to the text when omitted. */
  sort?: number | string
}

export interface LeagueRow {
  key: string
  /** First-column label; renders as a Link when href is present. */
  label: string
  href?: string | null
  /** Optional shop-chip links rendered after the label (wine-bev bands). */
  chips?: Array<{ href: string; text: string; title?: string }>
  cells: LeagueCell[]
}

export function LeagueTable({
  caption,
  firstHeader,
  headers,
  rows,
  minWidth = 720,
  defaultSortKey,
  defaultDir = 'desc',
}: {
  caption: string
  /** Header of the entity (first) column. */
  firstHeader: string
  /** Headers of the value columns, in cell order. */
  headers: string[]
  rows: LeagueRow[]
  minWidth?: number
  /** Column key to sort by initially — 'label' or `c<index>`. */
  defaultSortKey?: string
  defaultDir?: 'asc' | 'desc'
}) {
  const columns = [
    {
      key: 'label',
      header: firstHeader,
      numeric: false,
      sortValue: (r: LeagueRow) => r.label,
      // FRIDGE-1507 W1: the label and its chip group are items of one wrapping
      // flex row. The cell is `whitespace-nowrap`, so a chip group placed
      // inline after the label could not drop to a new line, and it took the
      // whole cell's width starting after the label: at 768 px the "shop ..."
      // chips ran 73 px out of their cell, over the "Models" figure. Now the
      // group wraps below the label when the two do not fit side by side.
      render: (r: LeagueRow) => {
        const label = r.href ? (
          // FRIDGE-1507 T: the 17px label link taps as 24px tall (tap-24).
          <Link href={r.href} className="tap-24 font-semibold text-[#0469f0] hover:underline">
            {r.label}
          </Link>
        ) : (
          <span className="font-semibold">{r.label}</span>
        )
        if (!r.chips || r.chips.length === 0) return label
        return (
          <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1 align-middle">
            {label}
            <span className="inline-flex flex-wrap gap-1">
              {r.chips.map((c) => (
                <Link
                  key={c.href}
                  href={c.href}
                  title={c.title}
                  className="tap-24 inline-flex items-center rounded-full border border-gray-200 px-2 py-0.5 text-[11px] font-semibold text-[#0469f0] hover:border-[#0469f0] transition-colors"
                >
                  {c.text}
                </Link>
              ))}
            </span>
          </span>
        )
      },
    },
    ...headers.map((header, j) => ({
      key: `c${j}`,
      header,
      numeric: true,
      sortValue: (r: LeagueRow) => r.cells[j]?.sort ?? r.cells[j]?.text ?? '',
      render: (r: LeagueRow) => r.cells[j]?.text ?? '',
    })),
  ]
  return (
    <SortableTable
      caption={caption}
      columns={columns}
      rows={rows}
      rowKey={(r) => r.key}
      minWidth={minWidth}
      defaultSortKey={defaultSortKey}
      defaultDir={defaultDir}
    />
  )
}
